// TODO: Deprecate
import { useMemo } from 'react';
import {
  useGetFlowIndex,
  useGetImpactedSessionIds,
  useGetTimelineSessions,
  type GetImpactedSessionIdsParams,
  type GetFlowIndexParams,
} from '../../utils/queryHooks';
import { formatFlowIndexData, processSessionTableData } from '../../utils/dataUtils';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';

const useSessionIndex = ({
  viewerId,
  clientId,
  interval,
  globalFilters,
  eventNames,
  fields,
  options,
  impacted,
  enabled,
}: GetImpactedSessionIdsParams & {
  impacted: boolean;
  fields: string[];
  options: Record<string, Record<string, string | boolean>[]>;
}) => {
  const {
    data: impactedSessions,
    error: impactedSessionsError,
    isFetching: impactedSessionsFetching,
    refetch: impactedSessionsRefetch,
  } = useGetImpactedSessionIds({
    viewerId,
    clientId,
    interval,
    globalFilters,
    eventNames,
    enabled: enabled && impacted,
  });
  const {
    data: sessions,
    error: sessionsError,
    isFetching: sessionsFetching,
    refetch: sessionsRefetch,
  } = useGetTimelineSessions(viewerId, interval, clientId, fields, enabled);

  const data = useMemo(
    () => processSessionTableData(sessions ?? [], impactedSessions ?? [], fields, options),
    [sessions, impactedSessions, fields, options],
  );

  return {
    data,
    loading: sessionsFetching || impactedSessionsFetching,
    refetch: () => Promise.all([sessionsRefetch(), impactedSessionsRefetch()]),
    error: sessionsError ?? impactedSessionsError,
  };
};

const useFlowIndex = ({
  fields,
  viewerId,
  clientId,
  interval,
  filter,
  flowMetadata,
  options,
  enabled,
}: Omit<GetFlowIndexParams, 'dimensions' | 'metrics' | 'flowIdentifier'> & {
  fields: string[];
  flowMetadata?: AppFlowMetadata;
  options: any;
}) => {
  const fieldsTypeMapping = useMemo(
    () =>
      new Map(
        Object.values(options)
          .flat()
          .map((it: any) => [it.key as string, it.type as 'metrics' | 'dimensions']),
      ),
    [options],
  );
  const flowFieldsMapping = useMemo(() => {
    if (!flowMetadata) return undefined;

    return {
      // TODO: Should get dimensions from flow metadata
      startTimestamp: 'flow_start_timestamp',
      endTimestamp: 'flow_end_timestamp',
      duration: flowMetadata.metrics.avg_complete_duration,
      success: flowMetadata.metrics.success,
      complete: flowMetadata.metrics.complete,
      flowWithErrors: flowMetadata.metrics.flows_with_errors,
      criticalErrorFlow: flowMetadata.metrics.abandoned_flows_with_errors,
      nonCriticalErrorFlow: flowMetadata.metrics.completed_flows_with_errors,
      init: flowMetadata.metrics.init,
    };
  }, [flowMetadata]);
  const dimensions = fields.filter((it) => fieldsTypeMapping.get(it) === 'dimensions');
  const metrics = fields.filter((it) => fieldsTypeMapping.get(it) === 'metrics');
  const flowTimelineDimensions = [
    flowFieldsMapping?.startTimestamp,
    flowFieldsMapping?.endTimestamp,
  ];
  const flowMetrics = [
    flowFieldsMapping?.duration,
    flowFieldsMapping?.success,
    flowFieldsMapping?.complete,
    flowFieldsMapping?.criticalErrorFlow,
    flowFieldsMapping?.nonCriticalErrorFlow,
    flowFieldsMapping?.init,
    flowFieldsMapping?.flowWithErrors,
  ];
  const queryDimensions = Array.from(
    new Set([...flowTimelineDimensions, ...dimensions].filter(Boolean) as string[]),
  );
  const queryMetrics = Array.from(
    new Set([...flowMetrics, ...metrics].filter(Boolean) as string[]),
  );

  const res = useGetFlowIndex({
    dimensions: queryDimensions,
    metrics: queryMetrics,
    viewerId,
    clientId,
    interval,
    flowIdentifier: flowMetadata?.identifier,
    enabled,
  });
  const impactedRes = useGetFlowIndex({
    dimensions: queryDimensions,
    metrics: queryMetrics,
    viewerId,
    clientId,
    interval,
    flowIdentifier: flowMetadata?.identifier,
    filter,
    enabled: enabled && !!filter?.flat().length,
  });
  const data = useMemo(
    () =>
      flowFieldsMapping
        ? formatFlowIndexData(
            res.data?.[0]?.data?.[0]?.results ?? [],
            impactedRes.data?.[0]?.data?.[0]?.results ?? [],
            fields,
            fieldsTypeMapping,
            flowFieldsMapping,
          )
        : [],
    [fields, res.data, impactedRes.data, fieldsTypeMapping, flowFieldsMapping],
  );

  return {
    data,
    loading: res.isFetching || impactedRes.isFetching,
    refetch: () => Promise.all([res.refetch(), impactedRes.refetch()]),
    error: res.error || impactedRes.error,
  };
};

export const useSessionTableData = (
  params: Omit<
    Parameters<typeof useSessionIndex>[0] & Parameters<typeof useFlowIndex>[0],
    'enabled' | 'globalFilters'
  > & { isFlowTimeline: boolean },
) => {
  const {
    viewerId,
    clientId,
    interval,
    filter,
    eventNames,
    fields,
    options,
    impacted,
    flowMetadata,
    isFlowTimeline,
  } = params;

  const sessionIndexData = useSessionIndex({
    viewerId,
    clientId,
    interval,
    globalFilters: filter,
    eventNames,
    fields,
    options,
    impacted,
    enabled: !isFlowTimeline,
  });

  const flowIndexData = useFlowIndex({
    viewerId,
    clientId,
    flowMetadata,
    fields,
    interval,
    filter,
    options,
    enabled: isFlowTimeline,
  });

  return isFlowTimeline ? flowIndexData : sessionIndexData;
};
